import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';

const teamMembersData = [
    {
      name: "Nathaniel Neibert",
      isOwner: true,
      title: "Managing Partner",
      image: "/nate_profile.jpg",
      bio: "With over 21 years of experience in the construction industry, Nathaniel is a seasoned Managing Partner at Freedom Contracting Group specializing in commercial and industrial developments. Nathaniel began his career as a union carpenter apprentice and progressed to leadership, gaining extensive field expertise.<br><br>Throughout his career, he has led complex multimillion-dollar large-scale projects and built a strong reputation for delivering high-quality results on schedule and within budget. Nathaniel excels in managing multiple construction sites simultaneously, coordinating teams, and ensuring that every project meets the highest standards of safety and efficiency. His leadership, problem solving skills, and deep industry knowledge make him a trusted partner to clients and contractors alike.<br><br>Nathaniel attended a four-year carpenter apprenticeship through UBC and is OSHA certified. He leads with a balance of practical field knowledge and strategic planning, valued by clients and partners for his problem-solving mindset, steady leadership and commitment to quality at every stage of the project.",
      specialties: ["Commercial & Industrial Development", "Multi-Site Project Management", "Team Leadership & Coordination", "Large-Scale Project Delivery", "OSHA Safety Compliance"],
      //credentials: "Licensed General Contractor, PMP Certified, OSHA 30-Hour",
      email: "nneibert@freedomcg.net",
      phone: "(630) 297-3392"
    },
    {
      name: "Scott R. Knudsen",
      isOwner: true,
      title: "Managing Partner",
      image: "/scott_profile.png",
      bio: "Scott is a Managing Partner of Freedom Contracting Group, a full-service general contracting firm specializing in commercial and industrial construction. With more than 20 years of hands-on experience, Scott began his career as an intern and steadily advanced through the industry, ultimately serving as a project executive before launching his own company in 2026.<br><br> Throughout his career, Scott has successfully completed over 5,000 projects, ranging from small-scale service work to multimillion-dollar developments. His extensive field knowledge, operational expertise, and commitment to excellence have earned him a reputation for delivering high-quality results across diverse project types.<br><br> Scott takes an honest, customer-first approach, focusing on cost-saving strategies, accurate and all-inclusive pricing, organized project management, and complete transparency. He is dedicated to meeting deadlines, maintaining clear communication, and ensuring every project is completed safely, efficiently, and to the highest standard.<br><br> He holds a Bachelor’s degree in Construction Management from Northern Michigan University which serves as the foundation for his technical understanding and leadership in the construction industry. ",
      specialties: ["General Contracting", "Cost Management & Budgeting", "Client Communication", "Project Scheduling & Delivery", "Quality Assurance"],
      //credentials: "OSHA 500 Trainer, CSP Certified Safety Professional, LEED AP",
      email: "sknudsen@freedomcg.net",
      phone: "(773) 805-0112"
    },
    {
      name: "Patrick Woodville",
      title: "Construction Superintendent",
      image: "/patrick_profile.png",
      bio: "Patrick Woodville is a U.S. Army veteran with 20 years of experience in the construction industry. He brings a hands-on perspective to his role as Construction Superintendent, combining practical construction knowledge with the discipline, teamwork, and dedication shaped by his military service.<br><br>Patrick is committed to maintaining a safe, organized jobsite, supporting quality workmanship, and keeping projects moving forward. He values clear communication, accountability, and strong working relationships with crews, subcontractors, and clients.",
      specialties: ["Jobsite Safety & Organization", "Quality Workmanship", "Project Coordination", "Crew & Subcontractor Communication", "Teamwork & Accountability"]
    }
  ];

function Team() {
  const [expandedBios, setExpandedBios] = useState({});

  const toggleBio = (index) => {
    setExpandedBios(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };



  // Rotate the owners first; employees always follow in their listed order.
  // Mark only owners with isOwner: true when adding future team members.
  const teamMembers = useMemo(() => {
    const owners = teamMembersData.filter(member => member.isOwner);
    const employees = teamMembersData.filter(member => !member.isOwner);
    if (Math.random() < 0.5) owners.reverse();
    return [...owners, ...employees];
  }, []);

  // Function to truncate bio for preview
  const getTruncatedBio = (bio, maxLength = 300) => {
    const plainText = bio.replace(/<br><br>/g, ' ').replace(/<br>/g, ' ');
    if (plainText.length <= maxLength) return { truncated: bio, needsTruncation: false };
    
    // Find a good break point (end of sentence or space)
    let breakPoint = plainText.lastIndexOf('. ', maxLength);
    if (breakPoint === -1 || breakPoint < maxLength * 0.5) {
      breakPoint = plainText.lastIndexOf(' ', maxLength);
    }
    if (breakPoint === -1) breakPoint = maxLength;
    
    // Get the truncated version from original bio
    let charCount = 0;
    let truncatedBio = '';
    const parts = bio.split(/<br><br>|<br>/);
    
    for (let i = 0; i < parts.length; i++) {
      if (charCount + parts[i].length <= breakPoint + 50) {
        truncatedBio += parts[i];
        charCount += parts[i].length;
        if (i < parts.length - 1 && charCount < breakPoint) {
          truncatedBio += '<br><br>';
          charCount += 2;
        }
      } else {
        // Truncate this part
        const remaining = breakPoint - charCount;
        if (remaining > 0) {
          const lastSpace = parts[i].lastIndexOf(' ', remaining);
          truncatedBio += parts[i].substring(0, lastSpace > 0 ? lastSpace : remaining);
        }
        break;
      }
    }
    
    return { truncated: truncatedBio + '...', needsTruncation: true };
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <SEO title="Our Team" path="/our-team" description="Meet Freedom Contracting Group’s team, bringing decades of construction experience, field leadership, and a commitment to quality and safety." breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Our Team', url: '/our-team' }]} />
      {/* Header with home link */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">
                <span className="text-red-600">Our</span>
                <span className="text-blue-900"> Team</span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">Meet the people behind Freedom Contracting Group</p>
            </div>
            <Link
              to="/"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Back to home"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 via-gray-900 to-red-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Leadership Built on Experience
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Our team combines decades of construction expertise with a commitment to American values. 
            We believe in building lasting relationships alongside lasting structures.
          </p>
        </div>
      </div>

      {/* Team Members */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-12">
          {teamMembers.map((member, index) => {
            const { truncated, needsTruncation } = getTruncatedBio(member.bio);
            const isExpanded = expandedBios[index];
            
            return (
              <div 
                key={index}
                className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-[1.01] transition-transform duration-300"
              >
                <div className="md:flex">
                  {/* Image */}
                  <div className="md:w-1/3">
                    <div className="relative h-full min-h-[400px]">
                      <img 
                        src={member.image}
                        alt={member.name}
                        className="absolute inset-0 w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="md:w-2/3 p-8 md:p-12">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-3xl font-bold text-blue-900 mb-2">
                          {member.name}
                        </h3>
                        <p className="text-xl text-red-600 font-semibold mb-4">
                          {member.title}
                        </p>
                      </div>
                    </div>

                    {/* Bio with Read More */}
                    <div className="mb-6">
                      <p 
                        className="text-gray-700 text-lg leading-relaxed"
                        dangerouslySetInnerHTML={{ 
                          __html: isExpanded || !needsTruncation ? member.bio : truncated 
                        }}
                      />
                      {needsTruncation && (
                        <button
                          onClick={() => toggleBio(index)}
                          className="mt-3 inline-flex items-center gap-1 text-blue-900 hover:text-red-600 font-semibold transition-colors"
                        >
                          {isExpanded ? (
                            <>
                              Read Less
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                              </svg>
                            </>
                          ) : (
                            <>
                              Read More
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Specialties */}
                    <div className="mb-6">
                      <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
                        Specialties
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {member.specialties.map((specialty, idx) => (
                          <span 
                            key={idx}
                            className="px-4 py-2 bg-blue-50 text-blue-900 rounded-full text-sm font-medium"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Credentials - only show if they exist */}
                    {member.credentials && (
                      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                          Credentials & Certifications
                        </h4>
                        <p className="text-gray-700 text-sm">
                          {member.credentials}
                        </p>
                      </div>
                    )}

                    {/* Contact */}
                    <div className="flex flex-wrap gap-4">
                      {member.email && <a 
                        href={`mailto:${member.email}`}
                        className="inline-flex items-center gap-2 text-blue-900 hover:text-red-600 font-medium transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                        </svg>
                        {member.email}
                      </a>}
                      {member.phone && <a 
                        href={`tel:${member.phone.replace(/\D/g, '')}`}
                        className="inline-flex items-center gap-2 text-blue-900 hover:text-red-600 font-medium transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                        </svg>
                        {member.phone}
                      </a>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Company Values Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">
            Our Core Values
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-3">Integrity First</h3>
              <p className="text-gray-600">
                We conduct business with honesty and transparency, building trust with every project.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-red-600 mb-3">Quality Craftsmanship</h3>
              <p className="text-gray-600">
                American-made quality in every detail, built to last for generations.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-3">People First</h3>
              <p className="text-gray-600">
                Our team and clients are family. Safety, respect, and relationships matter most.
              </p>
            </div>
          </div>
        </div>

        {/* Join Our Team CTA */}
        <div className="mt-20 bg-gradient-to-r from-blue-900 to-red-900 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Join Our Growing Team
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            We're always looking for talented, dedicated professionals who share our values. 
            If you're passionate about quality construction and American craftsmanship, we want to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:hello@freedomcg.net"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 hover:bg-gray-100 font-semibold py-4 px-8 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              View Open Positions
            </a>
            <a 
              href="mailto:hello@freedomcg.net"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold py-4 px-8 rounded-xl transition-all"
            >
              Contact HR
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Team;